import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useParkingQueue } from '../../hooks/parkingQueue';
import ParkingTabs, { ParkingTab } from './ParkingTabs';
import ParkingAreasOverview from './ParkingAreasOverview';
import ParkingQueueBoard from '../ParkingQueue/ParkingQueueBoard';
import ParkingQueueRulesBox from '../ParkingQueue/ParkingQueueRulesBox';
import { ParkingAreasSkeleton, ParkingQueueSkeleton } from './ParkingSkeletons';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import './Parking.css';

const TAB_PATHS: Record<ParkingTab, string> = { areas: '/parking', queue: '/parking-queue' };
const tabForPath = (pathname: string): ParkingTab => (pathname.startsWith('/parking-queue') ? 'queue' : 'areas');

/**
 * Main-nav Parkering: one page mounted at both /parking and /parking-queue,
 * so the tab is state and switching never remounts or refetches. The URL
 * follows the tab (and the browser back button moves the tab), while the
 * areas, units, leases and queue load once in useParkingQueue.
 */
const ParkingPage: React.FC = () => {
    const { pathname, search } = useLocation();
    const [tab, setTab] = useState<ParkingTab>(() => tabForPath(pathname));
    useEffect(() => { setTab(tabForPath(pathname)); }, [pathname]);
    useDocumentTitle(tab === 'queue' ? 'Parkeringskö' : 'Parkering');
    const queue = useParkingQueue();

    // The tabs are links; queue params (?entry=, ?spot=, ?new=) belong to the Kö tab only.
    const tabHrefs: Record<ParkingTab, string> = { areas: TAB_PATHS.areas, queue: TAB_PATHS.queue + search };

    const activeCount = queue.loading ? null : queue.entries.filter(e => e.status === 'active').length;

    return (
        <>
        <BreadcrumbComponent items={tab === 'queue' ? [{ text: 'Parkering', link: TAB_PATHS.areas }, { text: 'Parkeringskö' }] : [{ text: 'Parkering' }]} />
        <div className="parking-page parking-page-wrapper">
            <div className="parking-title-container">
                <h1 className="parking-page-title page-title">Parkering</h1>
            </div>
            <ParkingTabs active={tab} hrefs={tabHrefs} onSelect={setTab} activeCount={activeCount} />
            {tab === 'queue' && <ParkingQueueRulesBox />}
            {tab === 'queue'
                ? (queue.loading ? <ParkingQueueSkeleton /> : <ParkingQueueBoard queue={queue} />)
                : (queue.loading ? <ParkingAreasSkeleton /> : <ParkingAreasOverview queue={queue} />)}
        </div>
        </>
    );
};

export default ParkingPage;
